import { normalizePhoneForChatUserApi } from 'api/contacts-messaging';
import type { ChatUserModel } from 'types/chat-users/chat-user';
import type { WorkflowStatusId } from 'types/workflow/workflow-status';
import { dispatch, store } from 'store';
import { updateSingleChatUserByPhone } from 'store/slices/chat';
import { markChatUserUnreadByPhone } from 'api/chat-users';
import { updateSessionFlowStatus } from 'api/sessions';
import {
  isSnoozeTimePassed,
  isValidFutureSnoozeIso,
  normalizeSnoozeEndTimeFromApi,
} from 'utils/snooze-datetime';

export const WORKFLOW_OVERRIDE_EVENT = 'chatWorkflowOverride';

const STORAGE_KEY = 'chatWorkflowOverrides';
const SNOOZE_PERSIST_KEY = 'chatSnoozeUntilByPhone';

export type WorkflowOverride = {
  status?: WorkflowStatusId;
  snoozeUntilIso?: string | null;
};

/** מפתח אחיד ל-storage — מונע איבוד תזכורת אחרי רענון רשימה / שינוי תצוגה */
export function workflowPhoneKey(phone: string | null | undefined): string {
  if (!phone) return '';
  return normalizePhoneForChatUserApi(phone);
}

export function phonesMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const ka = workflowPhoneKey(a);
  const kb = workflowPhoneKey(b);
  return Boolean(ka && kb && ka === kb);
}

function normalizeOverrideMap(raw: Record<string, WorkflowOverride>): Record<string, WorkflowOverride> {
  const out: Record<string, WorkflowOverride> = {};
  for (const [k, v] of Object.entries(raw)) {
    const key = workflowPhoneKey(k);
    if (!key) continue;
    out[key] = { ...out[key], ...v };
  }
  return out;
}

function load(): Record<string, WorkflowOverride> {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        localStorage.setItem(STORAGE_KEY, raw);
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
    if (!raw) return {};
    return normalizeOverrideMap(JSON.parse(raw) as Record<string, WorkflowOverride>);
  } catch {
    return {};
  }
}

function save(data: Record<string, WorkflowOverride>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }
}

function loadSnoozePersist(): Record<string, string> {
  try {
    let raw = localStorage.getItem(SNOOZE_PERSIST_KEY);
    if (!raw) {
      raw = sessionStorage.getItem(SNOOZE_PERSIST_KEY);
      if (raw) {
        localStorage.setItem(SNOOZE_PERSIST_KEY, raw);
        sessionStorage.removeItem(SNOOZE_PERSIST_KEY);
      }
    }
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed)) {
      const key = workflowPhoneKey(k);
      if (key && v) out[key] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function saveSnoozePersist(data: Record<string, string>) {
  try {
    localStorage.setItem(SNOOZE_PERSIST_KEY, JSON.stringify(data));
  } catch {
    sessionStorage.setItem(SNOOZE_PERSIST_KEY, JSON.stringify(data));
  }
}

function syncSnoozeEndToRedux(phone: string, iso: string | null): void {
  void import('store/slices/chat').then(({ setSnoozeEndByPhone }) => {
    dispatch(setSnoozeEndByPhone({ phone, iso }));
  });
}

function getReduxSnoozeUntil(phone: string | null | undefined): string | null {
  const key = workflowPhoneKey(phone);
  if (!key) return null;
  return store.getState().chat.snoozeEndByPhone?.[key] ?? null;
}

/** שמירת מועד תזכורת — שורד רענון רשימה גם כשהשרת לא מחזיר snoozeEndTime */
export function setPersistedSnoozeUntil(phone: string, iso: string | null): void {
  const key = workflowPhoneKey(phone);
  if (!key) return;
  const data = loadSnoozePersist();
  if (iso) {
    data[key] = iso;
  } else {
    delete data[key];
  }
  saveSnoozePersist(data);
  syncSnoozeEndToRedux(phone, iso);
  dispatchChange();
}

export function getPersistedSnoozeUntil(phone: string | null | undefined): string | null {
  const key = workflowPhoneKey(phone);
  if (!key) return null;
  return loadSnoozePersist()[key] ?? null;
}

function clearPersistedSnoozeUntil(phone: string): void {
  setPersistedSnoozeUntil(phone, null);
}

function dispatchChange() {
  window.dispatchEvent(new Event(WORKFLOW_OVERRIDE_EVENT));
}

function allChatUsersFromStore(): ChatUserModel[] {
  const s = store.getState().chat;
  return [
    ...s.chatUsers.myChatUsers,
    ...s.chatUsers.mySnoozedChatUsers,
    ...s.chatUsers.notMyChatUsers,
    ...s.chatUsers.unassignedChatUsers,
    ...s.chatUsers.botsChatUsers,
    ...s.chatUsers.allChatUsers,
    ...s.chatUsers.customChatUsers,
    ...s.chatUsers.completedChatUsers,
  ];
}

function snoozeFromServerUser(
  phone: string | null | undefined,
  sourceUser?: ChatUserModel | null,
): string | null {
  const raw = sourceUser?.snoozeEndTime ?? findChatUserByPhone(phone ?? undefined)?.snoozeEndTime;
  return normalizeSnoozeEndTimeFromApi(raw);
}

function readActiveSnoozeIso(phone: string, data?: Record<string, WorkflowOverride>): string | null {
  const key = workflowPhoneKey(phone);
  if (!key) return null;

  const map = data ?? load();
  const fromOverride = map[key]?.snoozeUntilIso;
  if (isValidFutureSnoozeIso(fromOverride)) {
    return fromOverride!;
  }

  const persisted = getPersistedSnoozeUntil(phone);
  if (isValidFutureSnoozeIso(persisted)) {
    return persisted;
  }

  const fromRedux = getReduxSnoozeUntil(phone);
  if (isValidFutureSnoozeIso(fromRedux)) {
    return fromRedux;
  }

  return null;
}

/** חיפוש משתמש שיחה בליסטים לפי מספר טלפון — לסטטוס workflow מהשרת */
export function findChatUserByPhone(phone: string | null | undefined): ChatUserModel | undefined {
  if (!phone) return undefined;
  const s = store.getState().chat;
  if (phonesMatch(s.selectedChatUser?.UserDisplayPhoneNumber, phone)) {
    return s.selectedChatUser ?? undefined;
  }
  for (const u of allChatUsersFromStore()) {
    if (phonesMatch(u.UserDisplayPhoneNumber, phone)) return u;
  }
  return undefined;
}

function resolveUpdatePhone(phone: string): string {
  return findChatUserByPhone(phone)?.UserDisplayPhoneNumber ?? phone;
}

function workflowBaseFromUser(u: ChatUserModel | null | undefined): WorkflowStatusId | null {
  const c = u?.flowStatusCode;
  if (c != null && c >= 1 && c <= 6) return c as WorkflowStatusId;
  return null;
}

function workflowBaseFromServer(phone: string | null | undefined): WorkflowStatusId {
  if (!phone) return 1;
  const u = findChatUserByPhone(phone);
  return workflowBaseFromUser(u) ?? 1;
}

export function setWorkflowOverride(phone: string, patch: Partial<WorkflowOverride>): void {
  const key = workflowPhoneKey(phone);
  if (!key) return;

  const data = load();
  const prev = data[key] ?? {};
  const next: WorkflowOverride = { ...prev, ...patch };
  if (patch.snoozeUntilIso === null) {
    next.snoozeUntilIso = null;
  }
  data[key] = next;
  save(data);
  if (patch.snoozeUntilIso !== undefined) {
    setPersistedSnoozeUntil(phone, patch.snoozeUntilIso ?? null);
  }
  dispatchChange();
}

/** מחיקת override לטלפון — חזרה לסטטוס מהשרת */
export function clearWorkflowOverride(phone: string): void {
  const key = workflowPhoneKey(phone);
  if (!key) return;

  const data = load();
  if (!(key in data)) return;
  delete data[key];
  save(data);
  dispatchChange();
}

/** מחיקת override לשדה status בלבד (אחרי סנכרון משרת) — snoozeUntilIso נשמר */
export function clearWorkflowStatusOverride(phone: string): void {
  const key = workflowPhoneKey(phone);
  if (!key) return;

  const data = load();
  const prev = data[key];
  if (!prev || prev.status === undefined) return;
  const next: WorkflowOverride = { ...prev };
  delete next.status;
  if (next.snoozeUntilIso) {
    setPersistedSnoozeUntil(phone, next.snoozeUntilIso);
  }
  if (next.snoozeUntilIso == null && Object.keys(next).length === 0) {
    delete data[key];
  } else {
    data[key] = next;
  }
  save(data);
  dispatchChange();
}

function resolveSnoozeIfExpired(ov: WorkflowOverride): { next: WorkflowOverride; changed: boolean } {
  if (ov.status !== 5 || !ov.snoozeUntilIso) {
    return { next: ov, changed: false };
  }
  if (!isSnoozeTimePassed(ov.snoozeUntilIso)) {
    return { next: ov, changed: false };
  }
  return { next: { status: 2, snoozeUntilIso: null }, changed: true };
}

function clearSnoozeOverrideFields(data: Record<string, WorkflowOverride>, key: string): void {
  const prev = data[key];
  if (!prev) return;
  delete prev.snoozeUntilIso;
  delete prev.status;
  if (Object.keys(prev).length === 0) {
    delete data[key];
  } else {
    data[key] = prev;
  }
}

const snoozeExpiryInFlight = new Set<string>();

/** סנכרון תזכורת שפגה: סטטוס 2 + «סמן כלא נקראה» + API לשרת */
function applyExpiredSnooze(phone: string): void {
  const key = workflowPhoneKey(phone);
  if (!key || snoozeExpiryInFlight.has(key)) return;
  snoozeExpiryInFlight.add(key);

  const user = findChatUserByPhone(phone);
  const updatePhone = resolveUpdatePhone(phone);
  const sessionId = user?.SessionId?.trim();

  clearPersistedSnoozeUntil(phone);
  updateSingleChatUserByPhone(updatePhone, {
    flowStatusCode: 2,
    snoozeEndTime: null,
  });

  void markChatUserUnreadByPhone(updatePhone, user).finally(() => {
    snoozeExpiryInFlight.delete(key);
  });

  if (!sessionId || sessionId.startsWith('preview-session')) {
    return;
  }

  void updateSessionFlowStatus(sessionId, 2, null).then((res) => {
    if (res?.success && res.data) {
      updateSingleChatUserByPhone(updatePhone, {
        flowStatusCode: res.data.flowStatusCode as WorkflowStatusId,
        flowStatus: res.data.flowStatus,
        snoozeEndTime: null,
      });
    }
  });
}

function collectExpiredSnoozePhones(data: Record<string, WorkflowOverride>): Set<string> {
  const phones = new Set<string>();

  for (const key of Object.keys(data)) {
    const ov = data[key];
    if (!ov) continue;
    const { changed } = resolveSnoozeIfExpired(ov);
    if (changed) phones.add(key);
  }

  for (const user of allChatUsersFromStore()) {
    const phone = user.UserDisplayPhoneNumber;
    if (!phone || user.flowStatusCode !== 5) continue;
    const key = workflowPhoneKey(phone);
    const snoozeIso =
      snoozeFromServerUser(phone, user) ??
      data[key]?.snoozeUntilIso ??
      getPersistedSnoozeUntil(phone);
    if (isSnoozeTimePassed(snoozeIso)) {
      phones.add(key);
    }
  }

  return phones;
}

/** תאריך/שעת תזכורת — קודם מה-DB (snoozeEndTime), אחר כך optimistic מקומי */
export function getEffectiveSnoozeUntilIso(
  phone: string | null | undefined,
  sourceUser?: ChatUserModel | null,
): string | null {
  const fromServer = snoozeFromServerUser(phone, sourceUser);
  if (fromServer) {
    if (isSnoozeTimePassed(fromServer)) {
      if (phone) applyExpiredSnooze(phone);
      return null;
    }
    return fromServer;
  }

  const fromLocal = phone ? readActiveSnoozeIso(phone) : null;
  if (fromLocal) return fromLocal;

  const key = workflowPhoneKey(phone ?? '');
  const data = load();
  const ov = key ? data[key] : undefined;

  if (ov && phone) {
    const { next, changed } = resolveSnoozeIfExpired(ov);
    if (changed) {
      data[key] = next;
      save(data);
      dispatchChange();
      applyExpiredSnooze(phone);
      return null;
    }
  }

  return null;
}

export function getEffectiveWorkflowStatusForUser(
  phone: string | null | undefined,
  sourceUser?: ChatUserModel | null,
): WorkflowStatusId {
  const base = workflowBaseFromUser(sourceUser) ?? workflowBaseFromServer(phone);
  if (!phone) return base;

  const key = workflowPhoneKey(phone);
  const user = sourceUser ?? findChatUserByPhone(phone);
  if (user?.flowStatusCode === 5) {
    const snoozeIso =
      snoozeFromServerUser(phone, user) ??
      (key ? load()[key]?.snoozeUntilIso : null) ??
      getPersistedSnoozeUntil(phone);
    if (isSnoozeTimePassed(snoozeIso)) {
      applyExpiredSnooze(phone);
      return 2;
    }
  }

  const data = load();
  const ov = key ? data[key] : undefined;
  if (!ov) return base;

  const { next, changed } = resolveSnoozeIfExpired(ov);
  if (changed) {
    data[key] = next;
    save(data);
    dispatchChange();
    applyExpiredSnooze(phone);
    return 2;
  }

  if (ov.status !== undefined) return ov.status;
  return base;
}

/** ממזג snoozeEndTime מה-DB; fallback מקומי רק אם השרת לא החזיר */
export function mergeSnoozeTimesIntoChatUsers(users: ChatUserModel[]): ChatUserModel[] {
  return users.map((u) => {
    const phone = u.UserDisplayPhoneNumber;
    if (!phone) return u;

    const serverIso = snoozeFromServerUser(phone, u);
    if (serverIso) {
      if (u.snoozeEndTime === serverIso) return u;
      return { ...u, snoozeEndTime: serverIso };
    }

    if (u.flowStatusCode !== 5) return u;

    const iso = readActiveSnoozeIso(phone);
    if (!iso) return u;

    return { ...u, snoozeEndTime: iso };
  });
}

function reminderSortTimestamp(iso: string | null | undefined): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/** תצוגת תזכורת — מיון עולה לפי מועד התזכורת (הקרוב ביותר בראש הרשימה). */
export function sortChatUsersByReminderDateAsc(users: ChatUserModel[]): ChatUserModel[] {
  return [...users].sort((a, b) => {
    const aIso = getEffectiveSnoozeUntilIso(a.UserDisplayPhoneNumber, a);
    const bIso = getEffectiveSnoozeUntilIso(b.UserDisplayPhoneNumber, b);
    const diff = reminderSortTimestamp(aIso) - reminderSortTimestamp(bIso);
    if (diff !== 0) return diff;
    return (a.FullName ?? '').localeCompare(b.FullName ?? '', 'he');
  });
}

export function shouldSortChatUsersByReminderDate(
  viewId: string,
  workflowFilter: WorkflowStatusId | null,
): boolean {
  return viewId === 'mySnoozed' || workflowFilter === 5;
}

/** סריקת כל ההקפאות שפגו — לטיימר רקע; מסנכרן לשרת + סימון לא-נקרא */
export function expireSnoozesIfNeeded(): boolean {
  const data = load();
  const expiredPhones = collectExpiredSnoozePhones(data);
  if (expiredPhones.size === 0) return false;

  for (const key of expiredPhones) {
    const ov = data[key];
    if (ov) {
      const { next, changed } = resolveSnoozeIfExpired(ov);
      if (changed) {
        data[key] = next;
      } else {
        clearSnoozeOverrideFields(data, key);
      }
    }
    applyExpiredSnooze(key);
  }

  save(data);
  dispatchChange();
  return true;
}
